/**
 * useAdminPresence · v2.7.65+E142 (2026-02-03)
 * ─────────────────────────────────────────────
 * Shared hook that returns a live `{[username]: { status, presence, ... }}`
 * map. Two data sources feed the same in-memory cache:
 *   1. `/api/admin/presence` REST poll every 30 s (source of truth for
 *      users who came online since the last WS handshake).
 *   2. `ws://…/ws/dispatch` broadcast frames — the backend fan-outs
 *      `user.presence_changed` on every PATCH to /admin/me/presence,
 *      so every open Admin Console flips a teammate's dot the moment
 *      they hit "Meeting" / "DND" / "Vacation" without waiting for the
 *      next poll cycle.
 *
 * Only one WebSocket + one polling timer per browser session, no matter
 * how many components mount the hook.
 */
import { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";

const REFRESH_MS = 30_000;

const _state = {
  data: {},
  at: 0,
  subscribers: new Set(),
  fetching: false,
  timer: null,
  ws: null,
  wsRetry: 0,
};

function _notify() {
  // Snapshot copy so consumers using ref-equality re-render.
  const snap = { ..._state.data };
  _state.data = snap;
  _state.subscribers.forEach((cb) => cb(snap));
}

async function _fetchOnce() {
  if (_state.fetching) return;
  _state.fetching = true;
  try {
    const { data } = await adminApi.get("/admin/presence");
    _state.data = data?.presence || {};
    _state.at = Date.now();
    _notify();
  } catch { /* silent — keep prior map */ }
  finally { _state.fetching = false; }
}

function _ensureTimer() {
  if (_state.timer) return;
  _state.timer = setInterval(_fetchOnce, REFRESH_MS);
}

// v2.7.65+E142 · Persistent WS listener. Auto-reconnects with a capped
// exponential back-off so a dropped connection doesn't silently freeze
// the presence dots forever.
function _ensureWebSocket() {
  if (_state.ws && (_state.ws.readyState === WebSocket.OPEN || _state.ws.readyState === WebSocket.CONNECTING)) return;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  if (!base) return;
  const url = base.replace(/^http/, "ws") + "/ws/dispatch";
  try {
    const ws = new WebSocket(url);
    _state.ws = ws;
    ws.onopen = () => { _state.wsRetry = 0; };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg?.event !== "user.presence_changed") return;
        const uname = msg.username;
        if (!uname) return;
        const prev = _state.data[uname] || {};
        _state.data = {
          ..._state.data,
          [uname]: {
            ...prev,
            presence:      msg.presence || prev.presence,
            presence_note: typeof msg.note === "string" ? msg.note : prev.presence_note,
            // Any explicit presence change is a strong signal the user
            // is alive → mark status online so the widget surfaces them.
            status:        msg.presence === "offline" ? "offline" : "online",
            last_seen_at:  msg.at || prev.last_seen_at,
          },
        };
        _notify();
      } catch { /* ignore malformed */ }
    };
    ws.onclose = () => {
      _state.ws = null;
      _state.wsRetry = Math.min(_state.wsRetry + 1, 6);
      // 1s, 2s, 4s, 8s, 16s, 32s (capped).
      const delay = 1000 * Math.pow(2, _state.wsRetry - 1);
      setTimeout(_ensureWebSocket, delay);
    };
    ws.onerror = () => { /* swallow — onclose handles retry */ };
  } catch { /* WebSocket unsupported */ }
}


export default function useAdminPresence() {
  const [presence, setPresence] = useState(_state.data);

  useEffect(() => {
    _state.subscribers.add(setPresence);
    if (Date.now() - _state.at > REFRESH_MS) _fetchOnce();
    _ensureTimer();
    _ensureWebSocket();
    return () => {
      _state.subscribers.delete(setPresence);
    };
  }, []);

  return presence;
}


/** Lookup helper — pulls the presence entry for one username. */
export function usePresenceFor(username) {
  const map = useAdminPresence();
  if (!username) return null;
  return map[username] || null;
}
